import { useScript } from '../../../../../composables/useScript'
import type { ApplePaySessionConstructor } from '../../../../../types/apple-pay'
import { PaymentError } from '../../../types/PaymentError'

export const APPLE_PAY_LIBRARY_URL =
  'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js'

/** Load and expose the page-global Apple Pay library. */
export function useApplePayLibrary() {
  const script = useScript(
    {
      src: APPLE_PAY_LIBRARY_URL,
      crossorigin: 'anonymous',
    },
    {
      trigger: 'manual',
      use: () => window.ApplePaySession,
    },
  )

  async function getApplePayLibrary(): Promise<ApplePaySessionConstructor> {
    try {
      const library = await script.load()

      if (!library) {
        throw new PaymentError('Apple Pay library not loaded correctly', {
          type: '/errors/payment/unexpected',
        })
      }

      return library
    } catch (error) {
      if (error instanceof PaymentError) {
        throw error
      }

      throw new PaymentError('Apple Pay library failed to load', {
        cause: error,
        type: '/errors/payment/network-error',
      })
    }
  }

  /**
   * Returns the Apple Pay session synchronously.
   * /!\ Make sure to call this function after loading the library via the `getApplePayLibrary` function.
   */
  function requireApplePayLibrary(): ApplePaySessionConstructor {
    const library = window.ApplePaySession

    if (!library) {
      throw new PaymentError('Apple Pay library not loaded correctly', {
        type: '/errors/payment/unexpected',
      })
    }

    return library
  }

  return { getApplePayLibrary, requireApplePayLibrary }
}
